import { WorkspacePremium } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Link,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import MoneyInputWithBlur from '../../components/MoneyInputWithBlur';
import { formatCurrencyInCents } from '../../helpers/formatters';

const EditTransactionPage = () => {
    const navigate = useNavigate();
    const { transactionId } = useParams();
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [formData, setFormData] = useState({
        type: 'income',
        amount: 0,
        deduction: 0,
        net_amount: 0,
        description: '',
        payment_method: 'pix',
        order_id: '',
        authorization_id: '',
        source: '',
        outcome_category: 'expense',
    });
    const [errors, setErrors] = useState({});

    const transactionTypes = [
        { value: 'income', label: 'Entrada' },
        { value: 'outcome', label: 'Saída' }
    ];

    const paymentMethods = [
        { value: 'pix', label: 'PIX' },
        { value: 'credit_card', label: 'Cartão de Crédito' },
        { value: 'debit_card', label: 'Cartão de Débito' },
        { value: 'bank_transfer', label: 'Transferência Bancária' },
        { value: 'cash', label: 'Dinheiro' },
        { value: 'other', label: 'Outro' }
    ];

    // Carregar dados da transação
    useEffect(() => {
        const fetchTransaction = async () => {
            try {
                console.log('Carregando transação com ID:', transactionId);
                setLoadingData(true);
                const response = await api.get(`transactions/${transactionId}`);
                console.log('Resposta da API:', response.data);

                if (response.data.success) {
                    const transaction = response.data.data.transaction;
                    console.log('Dados da transação:', transaction);
                    setFormData({
                        type: transaction.type,
                        amount: transaction.amount / 100, // Converter de centavos para reais
                        deduction: transaction.deduction / 100,
                        net_amount: transaction.net_amount / 100,
                        description: transaction.description || '',
                        payment_method: transaction.payment_method,
                        order_id: transaction.order_id || '',
                        authorization_id: transaction.authorization_id || '',
                        source: transaction.source || '',
                        outcome_category: transaction.outcome_category === 'withdrawal'
                            ? 'withdrawal'
                            : 'expense',
                    });
                } else {
                    throw new Error(response.data.message || 'Erro ao carregar transação');
                }
            } catch (error) {
                console.error('Erro ao carregar transação:', error);
                await Swal.fire({
                    title: 'Erro!',
                    text: 'Erro ao carregar dados da transação',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                navigate('/admin/transactions');
            } finally {
                setLoadingData(false);
            }
        };

        if (transactionId) {
            fetchTransaction();
        }
    }, [transactionId, navigate]);

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = { ...prev, [field]: value };

            // Calcular deduction automaticamente baseado em amount e net_amount
            if (field === 'amount' || field === 'net_amount') {
                const amount = field === 'amount' ? (parseFloat(value) || 0) : newData.amount;
                const netAmount = field === 'net_amount' ? (parseFloat(value) || 0) : newData.net_amount;

                // Deduction = Amount - NetAmount
                const deduction = amount - netAmount;
                newData.deduction = deduction;
            }

            return newData;
        });

        // Limpar erro do campo quando o usuário começar a digitar
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.type) {
            newErrors.type = 'Tipo é obrigatório';
        }

        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = 'Valor é obrigatório e deve ser maior que zero';
        }

        if (!formData.payment_method) {
            newErrors.payment_method = 'Método de pagamento é obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const transactionData = {
                type: formData.type,
                amount: Math.round(formData.amount * 100), // Converter para centavos
                deduction: Math.round(formData.deduction * 100),
                net_amount: Math.round(formData.net_amount * 100),
                description: formData.description.trim(),
                payment_method: formData.payment_method,
                order_id: formData.order_id,
                authorization_id: formData.authorization_id.trim(),
                source: formData.source.trim(),
            };
            if (formData.type === 'outcome') {
                transactionData.outcome_category = formData.outcome_category === 'withdrawal'
                    ? 'withdrawal'
                    : 'expense';
            }

            const response = await api.put(`transactions/${transactionId}`, transactionData);

            if (response.data.success) {
                await Swal.fire({
                    title: 'Sucesso!',
                    text: 'Transação atualizada com sucesso!',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });

                navigate('/admin/transactions');
            } else {
                if (response.data.errors) {
                    setErrors(response.data.errors);
                } else {
                    throw new Error(response.data.message || 'Erro ao atualizar transação');
                }
            }
        } catch (error) {
            console.error('Erro ao atualizar transação:', error);
            await Swal.fire({
                title: 'Erro!',
                text: error.message || 'Erro ao atualizar transação',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/transactions');
    };

    if (loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/transactions')} to={'/admin/transactions'}>
                    Transações
                </Link>
                <Typography color="text.primary">Editar Transação</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4">
                    ✏️ Editar Transação
                </Typography>
                <WorkspacePremium color="warning" />
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h5">Informações da Transação</Typography>
                    </Grid>

                    {/* Tipo da Transação */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Tipo da Transação"
                            value={formData.type}
                            onChange={(e) => handleInputChange('type', e.target.value)}
                            error={!!errors.type}
                            helperText={errors.type}
                        >
                            {transactionTypes.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {formData.type === 'outcome' ? (
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Categoria da saída"
                                value={formData.outcome_category || 'expense'}
                                onChange={(e) => handleInputChange('outcome_category', e.target.value)}
                                helperText="Despesa e retirada baixam o saldo; a diferença é só a classificação"
                            >
                                <MenuItem value="expense">Despesa</MenuItem>
                                <MenuItem value="withdrawal">Retirada</MenuItem>
                            </TextField>
                        </Grid>
                    ) : null}

                    {/* Método de Pagamento */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Método de Pagamento"
                            value={formData.payment_method}
                            onChange={(e) => handleInputChange('payment_method', e.target.value)}
                            error={!!errors.payment_method}
                            helperText={errors.payment_method}
                        >
                            {paymentMethods.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h5">Valores</Typography>
                    </Grid>

                    {/* Valor (Amount) */}
                    <Grid item xs={12} md={6}>
                        <MoneyInputWithBlur
                            label="Valor"
                            value={formData.amount}
                            onChange={(values) => handleInputChange('amount', values.value)}
                        />
                        {errors.amount && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {errors.amount}
                            </div>
                        )}
                    </Grid>

                    {/* Dedução (calculada automaticamente) */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Dedução"
                            value={formData.deduction.toFixed(2)}
                            InputProps={{
                                readOnly: true,
                                style: {
                                    fontSize: '1rem'
                                }
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    backgroundColor: 'action.disabledBackground',
                                    color: 'text.primary'
                                }
                            }}
                        />
                    </Grid>

                    {/* Valor Líquido */}
                    <Grid item xs={12} md={6}>
                        <MoneyInputWithBlur
                            label="Valor Líquido"
                            value={formData.net_amount}
                            onChange={(values) => handleInputChange('net_amount', values.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h5">Descrição</Typography>
                    </Grid>

                    {/* Descrição */}
                    <Grid item xs={12}>
                        <Typography variant="h5">Conciliação</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="ID de autorização"
                            value={formData.authorization_id}
                            onChange={(e) => handleInputChange('authorization_id', e.target.value)}
                            helperText="Ex.: SOURCE_ID do Mercado Pago"
                            inputProps={{ maxLength: 64 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Origem"
                            value={formData.source}
                            onChange={(e) => handleInputChange('source', e.target.value)}
                            helperText="Ex.: Mercado Pago Point, Mercado Pago QR"
                            inputProps={{ maxLength: 80 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Descrição"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            helperText="Descreva a transação (opcional)"
                            placeholder="Ex: Venda de produto X, Pagamento de fornecedor, etc."
                        />
                    </Grid>

                    {/* Resumo dos Valores */}
                    {formData.amount > 0 && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Resumo da Transação:
                                </Typography>
                                <Typography variant="body2">
                                    • Valor: {formatCurrencyInCents(Math.round(formData.amount * 100))}
                                </Typography>
                                <Typography variant="body2">
                                    • Dedução: {formatCurrencyInCents(Math.round(formData.deduction * 100))} (calculada automaticamente)
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    • Valor Líquido: {formatCurrencyInCents(Math.round(formData.net_amount * 100))}
                                </Typography>
                            </Alert>
                        </Grid>
                    )}

                    {/* Botões */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: 2,
                                marginBottom: 8,
                                gap: 2
                            }}
                        >
                            {/* Espaço à esquerda */}
                            <div style={{ height: '1rem' }} />

                            {/* Botões responsivos */}
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                width: { xs: '100%', sm: 'auto' },
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={loading}
                                    size="large"
                                    fullWidth={{ xs: true, sm: false }}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    size="large"
                                    startIcon={loading ? <CircularProgress size={20} /> : null}
                                    fullWidth={{ xs: true, sm: false }}
                                >
                                    {loading ? 'Atualizando...' : 'Atualizar Transação'}
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </div>
    );
};

export default EditTransactionPage;
